import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ShieldCheck, Syringe } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import CarteiraVacinas from "@/components/pet-form/CarteiraVacinas";
import ControleParasitarios from "@/components/pet-form/ControleParasitarios";
import type { PetFormData, VacinaEntry, ParasitarioEntry } from "@/pages/CadastrarPet";

const initialFormData: PetFormData = {
  nome: "",
  tipo_pet: "",
  outro_tipo_pet: "",
  raca: "",
  sexo_pet: "",
  data_nascimento: "",
  peso: "",
  idade: "",
  idade_meses: "",
  microchipado: false,
  microchip_codigo: "",
  castrado: false,
  observacoes: "",
  toma_medicacao: false,
  medicacao: "",
  obs_medicacoes: "",
  possui_deficiencia: false,
  tipo_deficiencia: "",
  tem_alergia: false,
  alergias: "",
  alimentacao_especial: false,
  descricao_alimentacao: "",
  doencas: "",
  sociavel_pets: false,
  sociavel_criancas: false,
  agressivo: false,
  medo_chuva: false,
  medo_trovao_fogos: false,
  dorme_dentro: false,
  aceita_outros_pets: true,
  ja_ficou_hospedado: false,
  precisa_espaco_cercado: false,
  precisa_supervisao_constante: false,
  horario_refeicao: "",
  horario_passeio: "",
  objetos_essenciais: "",
  emergencia: "",
  contato_medico_vet: "",
  autoriza_vet: false,
  autoriza_plantao: false,
  vacinas: [],
  parasitas: [],
};

export default function CarteiraPet() {
  const { id } = useParams();
  const [formData, setFormData] = useState<PetFormData>(initialFormData);
  const [loading, setLoading] = useState(true);

  const updateField = useCallback(
    <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  useEffect(() => {
    async function loadPet() {
      try {
        setLoading(true);

        const resp = await fetch(`/react-api/pets/${id}`, {
          headers: { Accept: "application/json" },
          credentials: "include",
        });

        if (!resp.ok) {
          throw new Error("Erro ao carregar pet");
        }

        const data = await resp.json();

        const vacinasNormalizadas: VacinaEntry[] = Array.isArray(data.vacinas)
          ? data.vacinas.map((v: any, index: number) => ({
              id: String(v.id ?? crypto.randomUUID()),
              vacina_id: v.vacina_id ?? null,
              vacina_nome_custom: v.vacina_nome_custom ?? null,
              nome: v.nome ?? "",
              dose_num: Number(v.dose_num ?? index + 1),
              data_aplicacao: v.data_aplicacao ?? "",
              data_revacinar: v.data_revacinar ?? v.proxima_dose ?? "",
              fabricante: v.fabricante ?? v.laboratorio ?? "",
              lote: v.lote ?? "",
              via: v.via ?? "",
              peso: v.peso != null ? String(v.peso) : "",
              veterinario: v.veterinario ?? "",
              nome_veterinario: v.nome_veterinario ?? v.veterinario ?? "",
              validado: Number(v.validado ?? 0),
            }))
          : [];

        const parasitasNormalizados: ParasitarioEntry[] = Array.isArray(data.parasitas)
          ? data.parasitas.map((p: any) => ({
              id: String(p.id ?? crypto.randomUUID()),
              tipo: p.tipo ?? "",
              produto: p.produto ?? "",
              data_aplicacao: p.data_aplicacao ?? "",
              data_reaplicar: p.data_reaplicar ?? p.proxima_aplicacao ?? p.data_proxima ?? "",
              peso: p.peso != null ? String(p.peso) : "",
              dose: p.dose ?? "",
              observacoes: p.observacoes ?? "",
              validado: Number(p.validado ?? 0),
            }))
          : [];

        setFormData({
          ...initialFormData,
          nome: data.nome || "",
          tipo_pet: data.tipo_pet || "",
          outro_tipo_pet: data.outro_tipo_pet || "",
          raca: data.raca || "",
          sexo_pet: data.sexo_pet || "",
          data_nascimento: data.data_nascimento || "",
          peso: data.peso != null ? String(data.peso) : "",
          idade: data.idade != null ? String(data.idade) : "",
          idade_meses: data.idade_meses != null ? String(data.idade_meses) : "",
          microchipado: !!data.microchipado,
          microchip_codigo: data.microchip_codigo || "",
          castrado: !!data.castrado,
          vacinas: vacinasNormalizadas,
          parasitas: parasitasNormalizados,
        });
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar a carteira do pet.");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadPet();
    }
  }, [id]);

  if (loading) {
    return <div className="p-6">Carregando carteira...</div>;
  }

  return (
    <>
      <Header activeTab="tutores" isCompact hideAudienceNav />
      <div className="min-h-screen gradient-hero">
        <div className="container max-w-4xl py-6 px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <button
              type="button"
              onClick={() => {
                window.location.href = "/tutor/dashboard";
              }}
              className="hover:text-forest transition-smooth"
            >
              Dashboard
            </button>
            <span>/</span>
            <span className="text-forest font-semibold">Carteira de saúde</span>
          </nav>

          <div className="rounded-2xl bg-white/90 p-5 shadow-sm mb-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <ShieldCheck className="w-6 h-6 text-forest" />
                  Carteira de saúde de {formData.nome || "pet"}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Atualize vacinas, vermífugo, antipulgas e carrapaticida sem abrir o cadastro completo do pet.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  window.location.href = "/tutor/dashboard";
                }}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <CarteiraVacinas formData={formData} updateField={updateField} />
            <ControleParasitarios formData={formData} updateField={updateField} />
          </div>
        </div>
      </div>
    </>
  );
}
